import { useMemo, useState } from "react";
import type { StoredFile } from "../api";
import { ConfirmButton } from "../components/ConfirmButton";
import { Logo } from "../components/Logo";
import { StorageMini } from "../components/StorageMeter";
import { formatBytes } from "../format";
import { isInlineImage, isVideo } from "../note-media";
import { useDeleteFiles, useFiles, useStorage, type FileKind } from "../queries";
import { Link } from "../router";

const KINDS: Array<{ kind: FileKind; label: string }> = [
  { kind: "all", label: "All" },
  { kind: "pictures", label: "Pictures" },
  { kind: "videos", label: "Videos" },
  { kind: "files", label: "Files" },
];

/**
 * File manager: every picture, video and file stored with your notes, biggest first, so you can see
 * what takes space and delete files (the notes stay; a deleted picture just disappears from its note).
 */
export function Files() {
  const [kind, setKind] = useState<FileKind>("all");
  const [sort, setSort] = useState<"size" | "recent">("size");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const storage = useStorage();
  const files = useFiles(kind, sort);
  const remove = useDeleteFiles();

  const list = useMemo(() => files.data?.pages.flatMap((p) => p.files) ?? [], [files.data]);
  const total = files.data?.pages[0]?.total;
  const selectedBytes = list.filter((f) => selected.has(f.id)).reduce((n, f) => n + f.size, 0);
  const allSelected = list.length > 0 && list.every((f) => selected.has(f.id));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const change = (fn: () => void) => {
    setSelected(new Set());
    fn();
  };

  return (
    <main className="settings files-page">
      <header className="settings-header">
        <Link to="/" className="btn btn-small">
          ← Notes
        </Link>
        <Logo />
      </header>
      <h1>Files</h1>
      <p className="muted">
        Pictures, videos and files saved with your notes. Deleting a file frees its space; the note itself stays.
      </p>
      {storage.data && <StorageMini usage={storage.data} />}

      <div className="files-toolbar">
        <div className="segmented" role="tablist" aria-label="Kind">
          {KINDS.map((k) => (
            <button key={k.kind} role="tab" aria-selected={kind === k.kind} className={kind === k.kind ? "on" : ""} onClick={() => change(() => setKind(k.kind))}>
              {k.label}
            </button>
          ))}
        </div>
        <label className="files-sort">
          <span className="muted small">Sort</span>
          <select value={sort} onChange={(e) => change(() => setSort(e.target.value as "size" | "recent"))}>
            <option value="size">Largest first</option>
            <option value="recent">Newest first</option>
          </select>
        </label>
      </div>

      <div className="files-actions">
        <label className="check">
          <input
            type="checkbox"
            checked={allSelected}
            disabled={!list.length}
            onChange={() => setSelected(allSelected ? new Set() : new Set(list.map((f) => f.id)))}
          />
          <span className="muted small">
            {total ? `${total.count} ${total.count === 1 ? "file" : "files"} · ${formatBytes(total.bytes)}` : " "}
          </span>
        </label>
        {selected.size > 0 && (
          <ConfirmButton
            label={`Delete ${selected.size} selected (${formatBytes(selectedBytes)})`}
            confirmLabel={`Delete ${selected.size} ${selected.size === 1 ? "file" : "files"} for good`}
            busy={remove.isPending}
            onConfirm={() => remove.mutate([...selected], { onSuccess: () => setSelected(new Set()) })}
          />
        )}
      </div>
      {remove.isError && <p className="error">{(remove.error as Error).message}</p>}

      {files.isPending ? (
        <p className="muted">Loading…</p>
      ) : list.length === 0 ? (
        <p className="muted files-empty">No {kind === "all" ? "files" : kind} yet.</p>
      ) : (
        <ul className="files-grid">
          {list.map((f) => (
            <FileCard key={f.id} file={f} selected={selected.has(f.id)} onToggle={() => toggle(f.id)} />
          ))}
        </ul>
      )}
      {files.hasNextPage && (
        <button className="btn" onClick={() => files.fetchNextPage()} disabled={files.isFetchingNextPage}>
          {files.isFetchingNextPage ? "Loading…" : "Show more"}
        </button>
      )}
    </main>
  );
}

function FileCard({ file, selected, onToggle }: { file: StoredFile; selected: boolean; onToggle: () => void }) {
  const preview = !file.url ? (
    <span className="file-icon">📄</span>
  ) : isInlineImage(file.mime) ? (
    <img src={file.url} alt="" loading="lazy" />
  ) : isVideo(file.mime) ? (
    <video src={`${file.url}#t=0.1`} preload="metadata" muted playsInline />
  ) : (
    <span className="file-icon">📄</span>
  );
  return (
    <li className={`file-card ${selected ? "selected" : ""}`}>
      <button className="file-preview" onClick={onToggle} aria-pressed={selected} aria-label={`Select ${file.filename ?? "file"}`}>
        {preview}
        {isVideo(file.mime) && <span className="file-badge">▶ Video</span>}
        <span className="file-check" aria-hidden="true">
          {selected ? "✓" : ""}
        </span>
      </button>
      <div className="file-meta">
        <span className="file-name" title={file.filename ?? ""}>
          {file.filename ?? "file"}
        </span>
        <span className="file-size">{formatBytes(file.size)}</span>
      </div>
      <Link to={`/?${file.noteStatus === "trashed" ? "view=trashed&" : ""}note=${file.noteId}`} className="file-note muted small">
        {file.noteStatus === "trashed" ? "🗑 " : ""}
        {file.noteTitle || "Untitled note"}
      </Link>
      {file.url && (
        <a className="file-open small" href={file.url} target="_blank" rel="noreferrer">
          Open
        </a>
      )}
    </li>
  );
}
